import React from "react";
import PropTypes from "prop-types";
import { Dialog } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";

// Form dialog used by the add/edit screens.
// - Fixed width per `size`, so it never collapses to a narrow strip.
// - Header and footer stay put; only the form scrolls, so long forms never run
//   off the screen and Save is always reachable.
// - Phones get a full-width sheet anchored to the bottom.
// - Escape and the close/Cancel buttons close it. Clicking outside does not,
//   so a stray click can't throw away what was typed.
const SIZES = {
    sm: "sm:max-w-md",
    md: "sm:max-w-lg",
    lg: "sm:max-w-2xl",
};

const Modal = ({ show, title, description, onClose, onSubmit, submitLabel, cancelLabel, size, submitDisabled, children }) => {
    const handleKeyDown = (e) => {
        if (e.key === "Escape") {
            e.stopPropagation();
            onClose(e);
        }
    };

    return (
        <Dialog open={show} onClose={() => {}} className="relative z-50">
            <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-[1px]" aria-hidden="true" />

            <div className="fixed inset-0 flex items-end justify-center sm:items-center sm:p-6">
                <Dialog.Panel
                    onKeyDown={handleKeyDown}
                    className={`flex max-h-[92vh] w-full flex-col rounded-t-2xl bg-white shadow-2xl ring-1 ring-black/5 sm:max-h-[calc(100vh-3rem)] sm:rounded-2xl ${SIZES[size] || SIZES.md}`}
                >
                    <div className="flex shrink-0 items-start justify-between gap-4 border-b border-gray-100 px-6 py-4">
                        <div className="min-w-0">
                            <Dialog.Title className="text-lg font-semibold text-gray-900">{title}</Dialog.Title>
                            {description && (
                                <Dialog.Description className="mt-1 text-sm text-gray-500">{description}</Dialog.Description>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Close"
                            className="-mr-2 rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                        >
                            <XIcon className="h-5 w-5" aria-hidden="true" />
                        </button>
                    </div>

                    <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
                        {children}
                    </div>

                    <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-gray-100 bg-gray-50/80 px-6 py-4 sm:flex-row sm:justify-end sm:rounded-b-2xl">
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-lg bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-gray-300 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                        >
                            {cancelLabel}
                        </button>
                        <button
                            type="button"
                            onClick={onSubmit}
                            disabled={submitDisabled}
                            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {submitLabel}
                        </button>
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
};

Modal.propTypes = {
    show: PropTypes.bool.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    submitLabel: PropTypes.string,
    cancelLabel: PropTypes.string,
    size: PropTypes.oneOf(["sm", "md", "lg"]),
    submitDisabled: PropTypes.bool,
    data: PropTypes.object, // kept for existing callers; unused
    children: PropTypes.node,
};

Modal.defaultProps = {
    description: null,
    submitLabel: "Save",
    cancelLabel: "Cancel",
    size: "md",
    submitDisabled: false,
    data: null,
    children: null,
};

export default Modal;
